"use client"
import Header from './components/layout/Header'
import { Breadcrumb, Button, ConfigProvider, Spin } from 'antd'
import Sidebar from './components/layout/Sidebar'
import { BreadcrumbItemType, BreadcrumbSeparatorType } from 'antd/es/breadcrumb/Breadcrumb'
import { GlobalUtils } from './utils/global.utils'
import { HOME_NAV_ITEMS, MenuItem } from './config/nav-items.config'
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { useTitleDataStore } from '@/core/stores/title-data.store'
import { useUpdate } from '@/core/hooks/use-update.hook'
import { MODAL_THEME } from '@/core/themes/modal.theme'
import container from '@/lib/di/container'
import AuthRepo from '@/core/repos/auth.repo'
import { PagePermission, PermissionClaim, PERMISSIONS } from '@/core/enums/permissions.enum'
import { usePermissionStore } from '@/core/stores/permission.store'
import { Outlet, useLocation, useNavigate } from 'react-router'

const authRepo = container.get(AuthRepo);

export default function HomeLayout() {
  const authState = authRepo.useAuth();
  const {pathname} = useLocation();
  const titleData = useTitleDataStore();
  const permissionStore = usePermissionStore();
  const navigate = useNavigate();
  const claims = authRepo.useClaims();
  const userDetail = authRepo.useDetails();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [menuWidth, setMenuWidth] = useState(300);
  const [permission, setPermission] = useState<PagePermission>({
    view: false,
    add: false,
    edit: false,
    delete: false,
  });
  const [navigationData, setNavigationData] = useState(GlobalUtils.getCurrentNavData(HOME_NAV_ITEMS, pathname.substring(1).split("/"))??[]);




  // Generate breadcrumb based on navigation data
  function getBreadcrumb(data: MenuItem[]): Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] {
    let result = [{title: 'Trang chủ'}];
    if (data) {
      result = [...result, ...data.map(item => ({title: item.label}))]
    }
    return result;
  }

  function getTitle(data: MenuItem[]): string {
    return data[data.length - 1]?.label??"Trang chủ";
  }

  useEffect(() => {
    setNavigationData(GlobalUtils.getCurrentNavData(HOME_NAV_ITEMS, pathname.substring(1).split("/")));
  }, [pathname])

  useEffect(() => {
    document.title = getTitle(navigationData);
    titleData.setTitleText(getTitle(navigationData));
    titleData.setTitleBack(false, 0);
    titleData.setTitleActions({}, 0);
  }, [navigationData])

  function checkPerm(claims: PermissionClaim[], permission?: string) {
    if (!permission) return true;
    function check(permission: string) {
      return (claim: PermissionClaim) => {
        if (claim.id === PERMISSIONS.ADMIN.key) return true;
        return claim.id === permission
      }
    }
    return claims.findIndex(check(permission))!=-1;
  }

  useEffect(()=>{
    const nav = navigationData[navigationData.length - 1];
    const _permissions = userDetail?.userName.toLowerCase() === 'admin'
    ?{
      add: true,
      delete: true,
      edit: true,
      view: true
    }
    :{
      view: checkPerm(claims, nav.permissions?.view),
      add: checkPerm(claims, nav.permissions?.add),
      edit: checkPerm(claims, nav.permissions?.edit),
      delete: checkPerm(claims, nav.permissions?.delete),
    };
    setPermission(_permissions);
    permissionStore.setPagePermission(_permissions);
  }, [navigationData, claims])

  useEffect(()=>{
    let _menuItems: MenuItem[] = [];
    if (userDetail?.userName.toLowerCase() === "admin") {
      _menuItems = HOME_NAV_ITEMS;
    } else {
      [...HOME_NAV_ITEMS].forEach(_group => {
        const group = {..._group};
        if (!group.children) {
          _menuItems.push(group);
        } else {
          const children = group.children.filter(item => {
            return checkPerm(claims, item.permissions?.view);
          })
          if (children.length > 0) {
            group.children = children;
            _menuItems.push(group);
          }
        }
      })
    }
    setMenuItems([..._menuItems]);
  }, [claims])

  useUpdate(() => {
    if (collapsed) {
      setMenuWidth(80);
    } else {
      setMenuWidth(300);
    }
  }, [collapsed])

  return (
    <>
      {authState
      ?<section className='h-screen w-screen flex flex-col overflow-hidden'>
        <Header onCollapsed={()=>setCollapsed(!collapsed)} collapsed={collapsed}></Header>
        <section className='flex-1 flex flex-row'>
          <Sidebar selectedKeys={pathname.substring(1).split("/")} items={menuItems} collapsed={collapsed}></Sidebar>
          <div className='flex-1 flex flex-col'>
            <div className='h-[38px] border-b border-gray-300 flex flex-row items-center px-[23px] bshadow z-20'>
              <Breadcrumb
                separator="/"
                style={{fontSize: 14}}
                items={getBreadcrumb(navigationData)}
              />
            </div>
      
            <div className="bg-[#F0F2F5] p-2 overflow-y-scroll z-0" style={{
                width: `calc(100vw - ${menuWidth}px)`,
                height: `calc(100vh - 108px)`,
                transition: "width 300ms",
                transitionTimingFunction: "cubic-bezier(0.2, 0, 0, 1)"}}>
              <div className='relative w-full bg-white rounded border border-gray-300 bshadow'>
                <div className='flex flex-row items-center w-full border-b border-gray-300 rounded-t bg-white px-4 py-3 text-[16px] text-gray-900'>
                  {titleData.titleBack?<Button className='mr-2 mt-[2px]' type="text" size="small" icon={<ArrowLeftOutlined />} onClick={()=>navigate(-1)} />:null}
                  <p className='text-[20px] font-[700] text-slate-800'>{titleData.titleText}</p>
                  <div className='flex-1 flex flex-row justify-end'>
                    {permission.add&&titleData.titleActions.onPress&&<ConfigProvider theme={MODAL_THEME}>
                      <Button
                        type='primary'
                        icon={<PlusOutlined />}
                        style={{
                          background: "var(--success-button)",
                        }}
                        onClick={titleData.titleActions.onPress}
                        >{titleData.titleActions.text}</Button>
                    </ConfigProvider>}
                  </div>
                </div>
                {permission.view?<Outlet></Outlet>:<div className='w-full flex flex-col justify-start items-center h-[80vh]'>
                    <div className='mt-20'><Spin></Spin></div>
                </div>}
              </div>
            </div>
          </div>
        </section>
      </section>
      :<Spin spinning fullscreen></Spin>}
    </>
  )
}
